import { useCallback } from "react"
import { pushDrawer } from "@/lib/drawer-trail"
import { useDrawerNavContext } from "@/components/v2/drawer/drawer-nav-context"
import { useTranscriptViewer } from "@/components/v2/voice/transcript-viewer-context"
import type { VoiceCallRecord } from "@/types/v2/voice"

// "Open the source call" for a follow-up's "✨ from your call
// …" breadcrumb (CON-195). Discriminates the same way the
// timeline call row does:
//   - inside a drawer entry body → push `call:<id>` onto the
//     slideover trail so the panel opens WITHIN the drawer.
//   - on a show page / inbox thread → open the floating
//     transcript viewer.
// Returns `null` when neither path can open a call (no drawer
// + no viewer), so the caller can disable the affordance.
export function useOpenSourceCall(): ((voiceCallId: number) => void) | null {
  const drawerNav = useDrawerNavContext()
  const viewer = useTranscriptViewer()

  const open = useCallback(
    (voiceCallId: number) => {
      if (drawerNav) {
        pushDrawer("call", voiceCallId, drawerNav)
        return
      }
      // The viewer hydrates the full record from the
      // `voice_call:<id>` stream, so seed a minimal record.
      viewer?.openTranscript(minimalCallRecord(voiceCallId))
    },
    [drawerNav, viewer],
  )

  if (!drawerNav && !viewer) return null
  return open
}

function minimalCallRecord(id: number): VoiceCallRecord {
  return {
    id,
    direction: "outbound",
    status: "completed",
    callerNumber: null,
    calleeNumber: null,
    startedAt: null,
    answeredAt: null,
    endedAt: null,
    durationSeconds: null,
    recordingUrl: null,
    recordingPath: `/v2/voice_calls/${id}/recording`,
    recordingDurationSeconds: null,
    transcriptStatus: "ready",
    transcript: [],
    summary: null,
    summaryUnavailableReason: null,
    disposition: null,
    dispositionInferred: false,
    summaryNote: null,
    insightsStatus: null,
    analysisInFlight: false,
    insights: {},
    followUpSuggestions: [],
    contact: null,
    employee: null,
    deal: null,
  }
}
